import moment from 'moment';
import { useForm, Controller, useFieldArray, FormProvider } from 'react-hook-form';
import { PropTypes } from 'prop-types';
import React, { useEffect } from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Button } from 'reactstrap';
import { compose } from 'redux';
import DateTimePicker from '../../../core/components/DateTimePicker';
import SuggestedItemPicker from '../../../core/components/SuggestedItemPicker';
import { RequiredWrapper, TextArea } from '../../../core/formValidation';
import { toString } from '../../../core/utils';
import {
  PluralReclamationType,
  ReclamationIncidentArea,
  ReclamationStatus,
  ReclamationType,
  ReclamationWashType,
} from '../../reclamations/models';
import { selectReclamationDamageOptions } from '../../reclamations/reducer';
import CustomerDetails from './CustomerDetails';
import DamageInformation from './DamageInformation';
import RewashCauses from './RewashCauses';
import VehicleDetails from './VehicleDetails';
import { map } from 'lodash';

export const ReclamationForm = (props) => {
  // eslint-disable-next-line
  const {
    intl,
    type,
    carParts,
    history,
    employees,
    disabled,
    onReady,
    title,
    sites,
    isNew,
    match,
    fields,
  } = props;
  const defaultWashType =
    props?.initialValues && typeof props.initialValues.issueWashType === 'number'
      ? props.initialValues.issueWashType
      : ReclamationWashType.Exterior;
  const buildFormValues = (initialValues) => {
    const vehicleDetails = (initialValues && initialValues.vehicleDetails) || {};
    return {
      description: initialValues?.description || '',
      issueDate: initialValues?.issueDate || null,
      issueSiteId: initialValues?.issueSiteId || initialValues?.issueSite?.id || null,
      issueWashType:
        typeof initialValues?.issueWashType === 'number'
          ? initialValues.issueWashType
          : ReclamationWashType.Exterior,
      employeeIds: initialValues?.employeeIds || [],
      customerName: initialValues?.customerName || '',
      customerEmail: initialValues?.customerEmail || '',
      customerPhone: initialValues?.customerPhone || '',
      vehicleDetails: {
        make: vehicleDetails.make || '',
        model: vehicleDetails.model || '',
        year: vehicleDetails.year ?? null,
        color: vehicleDetails.color || '',
        vin: vehicleDetails.vin || '',
        plate: vehicleDetails.plate || '',
      },
      claimDamagePartId: initialValues?.claimDamagePartId ?? null,
      claimDamageAreaId: initialValues?.claimDamageAreaId ?? null,
      incidentDetails: initialValues?.incidentDetails || {},
      status: initialValues?.status ?? null,
    };
  };
  const rhfDefaultValues = buildFormValues(props?.initialValues);
  const methods = useForm({ defaultValues: rhfDefaultValues });
  const { control, watch, register, handleSubmit, reset } = methods;
  const [isEditing, setIsEditing] = React.useState(false);
  const {
    fields: employeeFields,
    append: appendEmployee,
    remove: removeEmployee,
  } = useFieldArray({ control, name: 'employeeIds' });

  // Reset form when initialValues change (e.g., when editing a different row)
  useEffect(() => {
    if (props.initialValues && !isEditing) {
      const washType =
        typeof props.initialValues.issueWashType === 'number'
          ? props.initialValues.issueWashType
          : ReclamationWashType.Exterior;
      reset(buildFormValues({ ...props.initialValues, issueWashType: washType }));
    }
  }, [props.initialValues, reset, isEditing]);

  useEffect(() => {
    if (props.change) props.change('issueWashType', defaultWashType);
    const subscription = watch((value, { name }) => {
      if (name && !isEditing) {
        setIsEditing(true);
      }
      if (name === 'issueWashType' && props.change) {
        props.change('issueWashType', value.issueWashType);
      }
    });
    return () => {
      if (subscription && subscription.unsubscribe) subscription.unsubscribe();
    };
  }, [watch, props.change, isEditing]);

  const renderSuggestion = (suggestion) => (
    <div className="suggestion">
      <span>{suggestion.name}</span>
      <small>Site number: {suggestion.extId}</small>
    </div>
  );

  const getRemainingEmployees = (currentIndex) => {
    const current = watch('employeeIds') || [];
    const selectedIds = current
      .map((e, idx) => (idx === currentIndex ? null : e && e.employeeId))
      .filter((id) => !!id);
    return employees.filter((e) => !selectedIds.includes(e.id));
  };

  // Derive affectedArea from selected car part via RHF
  const selectedCarPartId = watch('claimDamagePartId');
  const affectedArea = (() => {
    const parts = props.carParts || [];
    const part = parts.find((p) => String(p.id) === String(selectedCarPartId));
    return part && Array.isArray(part.options) ? part.options : undefined;
  })();

  return (
    <FormProvider {...methods}>
      <div data-testid="reclamationFormWrapper">
        <section>
          <h1>
            {isNew && type === ReclamationType.Claim
              ? intl.formatMessage({ id: 'addCustomerDetails' })
              : title}
          </h1>
          <div className="form-horizontal">
            {employees.length > 0 && (
              <div className="form-group row" key="0">
                <div className="col-sm-6">
                  {!!employeeFields &&
                    employeeFields.map((field, index) => {
                      const remaining = getRemainingEmployees(index);
                      const currentValue = watch(`employeeIds.${index}.employeeId`);
                      const selectedOption = employees.find((e) => e.id === currentValue);
                      return (
                        <div key={field.id} className="readings-form">
                          <button
                            type="button"
                            className="close multi-form-delete"
                            onClick={() => removeEmployee(index)}
                          >
                            <i className="icon icon-squared-cross" />
                          </button>
                          <div className="mt-3">{`Employee #${index + 1}`}</div>
                          <div className="form-group">
                            <RequiredWrapper value={currentValue}>
                              <Controller
                                name={`employeeIds.${index}.employeeId`}
                                control={control}
                                rules={{ required: true }}
                                render={({ field: { onChange, value } }) => (
                                  <select
                                    id="employeeId"
                                    data-testid="employeeid-reclaimation-form"
                                    className="form-control"
                                    value={value || ''}
                                    onChange={(e) => onChange(Number(e.target.value))}
                                  >
                                    {selectedOption && (
                                      <option id="selected" value={selectedOption.id}>
                                        {selectedOption.firstName} {selectedOption.lastName}
                                      </option>
                                    )}
                                    {remaining.map((e) => (
                                      <option id="remaining" key={e.id} value={e.id}>
                                        {e.firstName} {e.lastName}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              />
                            </RequiredWrapper>
                          </div>
                        </div>
                      );
                    })}
                  <button
                    type="button"
                    className="button"
                    data-testid="add-employee-reclamation-form"
                    onClick={() => {
                      const remaining = getRemainingEmployees(-1);
                      const firstId =
                        (remaining[0] && remaining[0].id) || (employees[0] && employees[0].id);
                      appendEmployee({ employeeId: firstId });
                    }}
                  >
                    {intl.formatMessage({ id: 'addEmployee' })}
                  </button>
                </div>
              </div>
            )}
            {[
              type === ReclamationType.Claim && !isNew && (
                <div className="form-group row" key="1">
                  <div className="col-sm-6">
                    <label htmlFor="status">Status</label>
                    <RequiredWrapper value={watch('status')}>
                      <Controller
                        name="status"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value } }) => (
                          <select
                            id="status"
                            className="form-control"
                            value={value ?? ReclamationStatus.Incomplete}
                            onChange={(e) => onChange(Number(e.target.value))}
                          >
                            <option value={ReclamationStatus.Incomplete} disabled>
                              {intl.formatMessage({ id: 'Incomplete' })}
                            </option>
                            <option value={ReclamationStatus.Open}>
                              {intl.formatMessage({ id: 'Open' })}
                            </option>
                            <option value={ReclamationStatus.Investigating}>
                              {intl.formatMessage({ id: 'Investigating' })}
                            </option>
                            <option value={ReclamationStatus.RepairApproved}>
                              {intl.formatMessage({ id: 'RepairApproved' })}
                            </option>
                            <option disabled>------</option>
                            <option value={ReclamationStatus.Completed}>
                              {intl.formatMessage({ id: 'Completed' })}
                            </option>
                            <option value={ReclamationStatus.Closed}>
                              {intl.formatMessage({ id: 'Closed' })}
                            </option>
                            <option value={ReclamationStatus.RepairDenied}>
                              {intl.formatMessage({ id: 'RepairDenied' })}
                            </option>
                          </select>
                        )}
                      />
                    </RequiredWrapper>
                  </div>
                </div>
              ),
              (type === ReclamationType.Rewash || type === ReclamationType.Claim) && (
                <div className="form-group row" key="2">
                  <div className="form-group col-sm-6">
                    <label htmlFor="description">Description</label>
                    <Controller
                      name="description"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <TextArea
                          input={{ value: value ?? '', onChange }}
                          className="form-control"
                          id="description"
                          type="text"
                          aria-describedby="descriptionHelp"
                          placeholder={intl.formatMessage({ id: 'description' })}
                          maxLength="255"
                          data-testid="description-reclaimation-form"
                        />
                      )}
                    />
                  </div>
                </div>
              ),
              type === ReclamationType.Claim && (
                <div className="form-group row" key="4">
                  <div className="form-group col-sm-6 d-flex flex-column">
                    <label htmlFor="issueDate">Incident date</label>
                    <RequiredWrapper value={watch('issueDate')}>
                      <Controller
                        name="issueDate"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <DateTimePicker
                            className="form-control"
                            id="issueDate"
                            showToday
                            type="text"
                            aria-describedby="descriptionHelp"
                            placeholder="Add issue date"
                            placeholderText={`${moment(Date.now()).format('MM/DD/YY h:mm a')}`}
                            timeProps={{
                              showTimeSelect: true,
                              timeFormat: 'hh:mm aa',
                              timeIntervals: 15,
                              dateFormat: 'MM/dd/YYY h:mm aa',
                              timeCaption: 'Time',
                            }}
                            input={{ value, onChange }}
                          />
                        )}
                      />
                    </RequiredWrapper>
                  </div>
                  {!isNew && (
                    <div className="form-group col-sm-6">
                      <label htmlFor="issueSite">Issue site</label>
                      <RequiredWrapper value={watch('issueSiteId')}>
                        <Controller
                          name="issueSiteId"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <SuggestedItemPicker
                              id="issueSiteId"
                              items={sites}
                              input={{ value, onChange }}
                              renderSuggestion={renderSuggestion}
                              placeholder="addIssueSite"
                            />
                          )}
                        />
                      </RequiredWrapper>
                    </div>
                  )}
                  <div className="form-group col-sm-6">
                    <label htmlFor="issueSite">Incident Wash Type</label>
                    <RequiredWrapper value={watch('issueWashType')}>
                      <Controller
                        name="issueWashType"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value } }) => (
                          <select
                            id="issueWashType"
                            className="form-control"
                            value={value}
                            onChange={(e) => onChange(Number(e.target.value))}
                          >
                            <option value={ReclamationWashType.Exterior}>
                              {intl.formatMessage({ id: 'exterior' })}
                            </option>
                            <option value={ReclamationWashType.Interior}>
                              {intl.formatMessage({ id: 'interior' })}
                            </option>
                            <option value={ReclamationWashType.Both}>
                              {intl.formatMessage({ id: 'both' })}
                            </option>
                          </select>
                        )}
                      />
                    </RequiredWrapper>
                  </div>
                </div>
              ),
            ]}
            <CustomerDetails />
            <VehicleDetails />
            {(!isNew || type === ReclamationType.Rewash) && [
              type === ReclamationType.Claim && (
                <div data-toggle="collapse" key="5">
                  <DamageInformation />
                  <div className="form-group row">
                    <div className="form-group col-sm-6">
                      <label htmlFor="affectedArea">Car Parts</label>
                      <RequiredWrapper value={watch('claimDamagePartId')}>
                        <Controller
                          name="claimDamagePartId"
                          control={control}
                          rules={{ required: true }}
                          render={({ field: { onChange, value } }) => (
                            <select
                              id="carPart"
                              className="form-control"
                              value={value || ''}
                              onChange={(e) => onChange(Number(e.target.value))}
                            >
                              {(props.carParts || []).map((e) => (
                                <option key={e.id} value={e.id}>
                                  {e.value}
                                </option>
                              ))}
                            </select>
                          )}
                        />
                      </RequiredWrapper>
                    </div>
                    {affectedArea !== undefined && (
                      <div className="form-group col-sm-6">
                        <label htmlFor="affectedArea">Affected Area</label>
                        <RequiredWrapper value={watch('claimDamageAreaId')}>
                          <Controller
                            name="claimDamageAreaId"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { onChange, value } }) => (
                              <select
                                id="affectedArea"
                                className="form-control"
                                value={value || ''}
                                onChange={(e) => onChange(Number(e.target.value))}
                              >
                                {(affectedArea || []).map((e) => (
                                  <option key={e.id} value={e.id}>
                                    {e.value}
                                  </option>
                                ))}
                              </select>
                            )}
                          />
                        </RequiredWrapper>
                      </div>
                    )}
                  </div>
                </div>
              ),
              type === ReclamationType.Rewash && <RewashCauses key="6" />,
            ]}
          </div>
          <Button
            color="secondary"
            className="button"
            onClick={() => history.push(`/${match.params.site}/claims-old`)}
          >
            Cancel
          </Button>{' '}
          <Button
            color="primary"
            className="button confirm"
            onClick={handleSubmit((formValues) => {
              const payload = {
                ...props.initialValues,
                ...formValues,
                employeeIds: formValues.employeeIds,
              };

              if (props.isNew) {
                delete payload.id;
              }

              if (payload && payload.vehicleDetails) {
                const y = payload.vehicleDetails.year;
                if (y === '' || y === undefined) {
                  payload.vehicleDetails.year = null;
                } else if (y !== null) {
                  const n = Number(y);
                  payload.vehicleDetails.year = Number.isNaN(n) ? null : parseInt(String(n), 10);
                }
              }
              props.onSubmit(payload);
            })}
            disabled={disabled}
          >
            Ok
          </Button>
        </section>
      </div>
    </FormProvider>
  );
};

ReclamationForm.propTypes = {
  intl: PropTypes.shape({}).isRequired,
  employees: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
    })
  ),
  sites: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  type: PropTypes.number,
  carParts: PropTypes.arrayOf(PropTypes.shape({})),
  affectedArea: PropTypes.arrayOf(PropTypes.shape({})),
  onReady: PropTypes.func,
  disabled: PropTypes.bool,
  title: PropTypes.string,
  history: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      site: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  isNew: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => {
  return {
    carParts: selectReclamationDamageOptions(state),
  };
};
// TODO: add validator

export default compose(injectIntl, connect(mapStateToProps, null), withRouter)(ReclamationForm);
